import type { User } from '@creator/sdk/modules/account/account.model';
import { cn } from '@creator/ui/utils/ui';
import HorizontalScrollable from '@src/basic-components/HorizontalScrollable/HorizontalScrollable';
import { useStoreActions, useStoreState } from '@src/model/hooks';
import { getUser } from '@src/model/user/helpers';
import { storeEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';
import PartnerCard from './PartnerCard';

const PAGE_SIZE = 100;
const MOCK_USERS = ['mock-user-1', 'mock-user-2', 'mock-user-3'];

export interface PartnerListProps {
    tokenName: string;
    className?: string;
}

const PartnerList: FC<PartnerListProps> = ({ tokenName, className = '' }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Retrieve the load action and current token partners.
    const loadTokenPartners = useStoreActions(actions => actions.token.loadTokenPartners);
    // Note: We don't provide a fallback here so we can tell whether the partners have been loaded.
    const tokenPartners = useStoreState(state => state.token.tokenPartners?.[tokenName], storeEqualityFn);

    useEffect(() => {
        // Load only if token partners haven't been loaded yet.
        if (!tokenPartners) {
            const loadUsers = async () => {
                setIsLoading(true);
                await loadTokenPartners({ limit: PAGE_SIZE, tokenName });
                setIsLoading(false);
            };
            loadUsers();
        } else {
            // When token partners exist (even if empty), we mark loading as false.
            setIsLoading(false);
        }
    }, [tokenName, tokenPartners, loadTokenPartners]);

    const getPartner = (id: string): User => {
        if (id.includes('mock-user')) return { id, displayName: id } as User;

        return getUser(id);
    };

    const renderPartner = (userId: string) => {
        const user = getPartner(userId);
        if (!user) return null;

        return <PartnerCard key={user.id} className={`${!isLoading ? 'grayscale hover:grayscale-0' : ''}`} userId={user.id} isLoading={isLoading} tokenName={tokenName} />;
    };

    const renderPartners = () => {
        // If loading, render mock users; otherwise, render the token partners (or an empty array if no partners exist).
        const partnersToRender = isLoading ? MOCK_USERS : (tokenPartners ?? []);
        return partnersToRender.map(renderPartner);
    };

    return (
        <HorizontalScrollable
            id="partner-list"
            isLoadingContent={isLoading}
            scrollOffset={400}
            scrollContainerClassname="flex px-4 items-center space-x-3 lg:space-x-5"
            className={cn('text-gray-500 min-h-[110px] lg:min-h-[124px]', className)}
        >
            {renderPartners()}
        </HorizontalScrollable>
    );
};

export default memo(PartnerList);
